import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Dialog,
  DialogContent,
  Alert,
  IconButton,
  Fab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useTheme,
  alpha
} from '@mui/material';
import { auth, db, storage } from '../firebase';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import BottomNav from './BottomNav';
import { collection, query, where, orderBy, addDoc, deleteDoc, doc, Timestamp, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytesResumable, deleteObject, getDownloadURL } from 'firebase/storage';
import { format } from 'date-fns';

interface UploadedDocument {
  id: string;
  userId: string;
  fileName: string;
  fileType: string;
  fileUrl: string;
  uploadedAt: any;
  fileSize: number;
  storagePath: string;
  accounting_posting_status: 'uploaded' | 'working' | 'posted' | 'error' | 'duplicate';
  errorMessage?: string;
}

interface GroupedDocuments {
  [monthYear: string]: UploadedDocument[];
}

const DocumentsView: React.FC = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [documents, setDocuments] = useState<UploadedDocument[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<UploadedDocument | null>(null);
  const [expandedMonths, setExpandedMonths] = useState<{ [key: string]: boolean }>({});
  const isFirstLoad = useRef(true);

  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const docsRef = collection(db, 'documents');
    const q = query(
      docsRef,
      where('userId', '==', currentUser.uid),
      orderBy('uploadedAt', 'desc')
    );

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const docs: UploadedDocument[] = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as UploadedDocument));

        setDocuments(docs);
        setLoading(false);

        // Auto-expand current month on first load only
        if (docs.length > 0 && isFirstLoad.current) {
          const currentMonthKey = format(new Date(), 'MMMM yyyy');
          setExpandedMonths({ [currentMonthKey]: true });
          isFirstLoad.current = false;
        }
      },
      (error) => {
        console.error('Error fetching documents:', error);
        setError('Failed to load documents');
        setLoading(false);
      }
    );

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, []);

  const groupDocumentsByMonth = (): GroupedDocuments => {
    const grouped: GroupedDocuments = {};

    documents.forEach(doc => {
      const date = doc.uploadedAt?.toDate ? doc.uploadedAt.toDate() : new Date(doc.uploadedAt);
      const monthYear = format(date, 'MMMM yyyy');

      if (!grouped[monthYear]) {
        grouped[monthYear] = [];
      }
      grouped[monthYear].push(doc);
    });

    return grouped;
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const currentUser = auth.currentUser;
    if (!currentUser) return;

    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);
      setError(null);

      const timestamp = Date.now();
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const storagePath = `documents/${currentUser.uid}/${timestamp}_${sanitizedFileName}`;
      const storageRef = ref(storage, storagePath);

      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          console.error('Upload error:', error);
          setError('Failed to upload file');
          setUploading(false);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

          await addDoc(collection(db, 'documents'), {
            userId: currentUser.uid,
            fileName: file.name,
            fileType: file.type,
            fileUrl: downloadURL,
            fileSize: file.size,
            storagePath: storagePath,
            uploadedAt: Timestamp.now(),
            accounting_posting_status: 'uploaded'
          });

          // Real-time listener will automatically update the UI
          setUploading(false);
          setUploadProgress(0);
        }
      );
    } catch (error) {
      console.error('Error uploading file:', error);
      setError('Failed to upload file');
      setUploading(false);
    }
  };

  const handleDelete = async (document: UploadedDocument) => {
    if (!window.confirm(`Delete "${document.fileName}"?`)) return;

    try {
      const storageRef = ref(storage, document.storagePath);
      await deleteObject(storageRef);
      await deleteDoc(doc(db, 'documents', document.id));
      // Real-time listener will automatically update the UI
    } catch (error) {
      console.error('Error deleting document:', error);
      setError('Failed to delete document');
    }
  };

  const handlePreview = (doc: UploadedDocument) => {
    setPreviewDoc(doc);
    setPreviewOpen(true);
  };

  const isImageFile = (fileType: string): boolean => {
    return fileType.startsWith('image/');
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (timestamp: any): string => {
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    return format(date, 'MMM d, yyyy • h:mm a');
  };

  const handleAccordionChange = (monthYear: string) => {
    setExpandedMonths(prev => ({
      ...prev,
      [monthYear]: !prev[monthYear]
    }));
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) {
      return <ImageIcon sx={{ color: theme.palette.primary.main }} />;
    } else if (fileType === 'application/pdf') {
      return <PictureAsPdfIcon sx={{ color: '#f44336' }} />;
    } else {
      return <InsertDriveFileIcon sx={{ color: theme.palette.text.secondary }} />;
    }
  };

  const getStatusConfig = (status: string) => {
    const configs = {
      uploaded: {
        label: 'Uploaded',
        color: '#10b981',
        bgcolor: 'rgba(16, 185, 129, 0.1)',
      },
      working: {
        label: 'Working',
        color: '#f59e0b',
        bgcolor: 'rgba(245, 158, 11, 0.1)',
      },
      posted: {
        label: 'Posted',
        color: '#6366f1',
        bgcolor: 'rgba(99, 102, 241, 0.1)',
      },
      error: {
        label: 'Error',
        color: '#ef4444',
        bgcolor: 'rgba(239, 68, 68, 0.1)',
      },
      duplicate: {
        label: 'Duplicate',
        color: '#ef4444',
        bgcolor: 'rgba(239, 68, 68, 0.1)',
      },
    };
    return configs[status as keyof typeof configs] || configs.uploaded;
  };

  const groupedDocuments = groupDocumentsByMonth();
  const monthKeys = Object.keys(groupedDocuments);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', pb: 10 }}>
      {/* Modern Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '0 0 32px 32px',
          pt: 3,
          pb: 4,
          px: 2,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v6h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.3,
          },
        }}
      >
        <Container maxWidth="sm">
          <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', zIndex: 1 }}>
            <Box>
              <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}>
                My Documents
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                {documents.length} document{documents.length !== 1 ? 's' : ''} uploaded
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="sm" sx={{ mt: 3, position: 'relative', zIndex: 2 }}>
        {/* Error Message */}
        {error && (
          <Alert
            severity="error"
            onClose={() => setError(null)}
            sx={{
              mb: 3,
              borderRadius: '16px',
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)'
            }}
          >
            {error}
          </Alert>
        )}

        {/* Upload Progress Card */}
        {uploading && (
          <Card
            sx={{
              mb: 3,
              borderRadius: '20px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CircularProgress size={24} sx={{ mr: 2 }} />
                <Typography variant="body2" fontWeight={600}>
                  Uploading... {uploadProgress.toFixed(0)}%
                </Typography>
              </Box>
              <Box
                sx={{
                  width: '100%',
                  height: 8,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  borderRadius: 4,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    width: `${uploadProgress}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                    transition: 'width 0.3s ease',
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Documents List */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={48} />
          </Box>
        ) : documents.length === 0 ? (
          <Card
            elevation={0}
            sx={{
              borderRadius: '24px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              background: 'white'
            }}
          >
            <CardContent>
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '20px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                    mb: 3,
                    boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                  }}
                >
                  <DescriptionIcon sx={{ fontSize: 40, color: 'white' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  No documents yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Upload your first document to get started
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <Box>
            {monthKeys.map((monthYear) => (
              <Accordion
                key={monthYear}
                expanded={expandedMonths[monthYear] || false}
                onChange={() => handleAccordionChange(monthYear)}
                sx={{
                  mb: 2,
                  borderRadius: '20px !important',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  '&:before': {
                    display: 'none',
                  },
                  '&.Mui-expanded': {
                    margin: '0 0 16px 0',
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    borderRadius: '20px',
                    minHeight: 64,
                    '&.Mui-expanded': {
                      borderRadius: '20px 20px 0 0',
                      minHeight: 64,
                    },
                    '& .MuiAccordionSummary-content': {
                      my: 2,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1 }}>
                      {monthYear}
                    </Typography>
                    <Chip
                      label={`${groupedDocuments[monthYear].length} file${groupedDocuments[monthYear].length !== 1 ? 's' : ''}`}
                      size="small"
                      sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        fontWeight: 600,
                        mr: 2,
                      }}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <List sx={{ p: 0 }}>
                    {groupedDocuments[monthYear].map((doc, index) => (
                      <ListItem
                        key={doc.id}
                        sx={{
                          borderTop: index === 0 ? 'none' : '1px solid',
                          borderColor: 'divider',
                          py: 2,
                          px: 3,
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.02),
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          {getFileIcon(doc.fileType)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '180px',
                              }}
                            >
                              {doc.fileName}
                            </Typography>
                          }
                          secondary={
                            <Box sx={{ mt: 0.5 }}>
                              <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                  {formatDate(doc.uploadedAt)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  • {formatFileSize(doc.fileSize)}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Chip
                                  label={getStatusConfig(doc.accounting_posting_status).label}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    color: getStatusConfig(doc.accounting_posting_status).color,
                                    bgcolor: getStatusConfig(doc.accounting_posting_status).bgcolor,
                                    border: `1px solid ${getStatusConfig(doc.accounting_posting_status).color}`,
                                  }}
                                />
                                {doc.errorMessage && (
                                  <Typography variant="caption" sx={{ color: '#ef4444', fontWeight: 500 }}>
                                    • {doc.errorMessage}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          }
                        />
                        <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => handlePreview(doc)}
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.2),
                              },
                            }}
                          >
                            <VisibilityIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(doc)}
                            disabled={doc.accounting_posting_status !== 'uploaded'}
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: doc.accounting_posting_status === 'uploaded'
                                ? alpha(theme.palette.error.main, 0.1)
                                : 'rgba(0, 0, 0, 0.05)',
                              '&:hover': {
                                bgcolor: doc.accounting_posting_status === 'uploaded'
                                  ? alpha(theme.palette.error.main, 0.2)
                                  : 'rgba(0, 0, 0, 0.05)',
                              },
                              '&.Mui-disabled': {
                                bgcolor: 'rgba(0, 0, 0, 0.05)',
                              },
                            }}
                          >
                            <DeleteIcon
                              sx={{
                                fontSize: 16,
                                color: doc.accounting_posting_status === 'uploaded'
                                  ? theme.palette.error.main
                                  : 'rgba(0, 0, 0, 0.26)'
                              }}
                            />
                          </IconButton>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </Container>

      {/* Upload FAB */}
      <input
        accept="image/*,application/pdf"
        style={{ display: 'none' }}
        id="upload-file"
        type="file"
        onChange={handleFileUpload}
        disabled={uploading}
      />
      <label htmlFor="upload-file">
        <Fab
          component="span"
          disabled={uploading}
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 16,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
              boxShadow: '0 12px 32px rgba(102, 126, 234, 0.5)',
            },
            '&.Mui-disabled': {
              background: 'rgba(0, 0, 0, 0.12)',
            },
          }}
        >
          <AddIcon />
        </Fab>
      </label>

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '24px',
            maxHeight: '90vh',
          },
        }}
      >
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            p: 2,
          }}
        >
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => setPreviewOpen(false)}
            sx={{ color: 'white', mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: 'white', fontWeight: 600 }}>
            {previewDoc?.fileName}
          </Typography>
        </Box>
        <DialogContent sx={{ p: 0, bgcolor: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {previewDoc && (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {isImageFile(previewDoc.fileType) ? (
                <img
                  src={previewDoc.fileUrl}
                  alt={previewDoc.fileName}
                  style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                />
              ) : (
                <iframe
                  src={previewDoc.fileUrl}
                  title={previewDoc.fileName}
                  style={{ width: '100%', height: '70vh', border: 'none' }}
                />
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Bottom Navigation */}
      <BottomNav />
    </Box>
  );
};

export default DocumentsView;
